import { useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const EMPTY = { masked_name: "", bank: "", account_number: "", currency: "IDR", gl_account_code: "" };

// Adding a bank account and its statements from the screen. The statement is sent as the bank's own PDF;
// the server reads the PDF's text layer (never OCR) and the statement decides its period, balances and
// account number. A refusal is shown verbatim because it says what to fix.
export default function BankSources({ id, selected, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [refusal, setRefusal] = useState("");

  const createAccount = async () => {
    if (!form.masked_name.trim()) return toast.error("Nama tampil rekening wajib diisi.");
    const body = Object.fromEntries(Object.entries(form).filter(([, v]) => String(v).trim() !== ""));
    try {
      const { data } = await api.post(`/engagements/${id}/bank-accounts`, body);
      toast.success("Rekening bank ditambahkan");
      setForm(EMPTY);
      onChanged && onChanged(data.id);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const upload = async () => {
    if (!selected) return toast.error("Pilih rekening bank terlebih dahulu.");
    if (!file) return toast.error("Pilih berkas rekening koran (PDF).");
    setBusy(true); setRefusal(""); setResult(null);
    const payload = new FormData();
    payload.append("file", file);
    try {
      const { data } = await api.post(`/engagements/${id}/bank-accounts/${selected}/statements`, payload);
      setResult(data); setFile(null);
      toast.success("Rekening koran terdaftar. Unggah bukan berarti rekonsiliasi selesai.");
      onChanged && onChanged(selected);
    } catch (e) {
      setRefusal(getErrorMessage(e));
    } finally { setBusy(false); }
  };

  const field = (key, label, width = "w-40") => (
    <label className="text-xs"><span className="block text-muted-foreground mb-1">{label}</span>
      <Input className={`h-9 ${width} max-w-full`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
             data-testid={`bank-new-${key}`} />
    </label>
  );

  return (
    <div className="bg-white border border-border rounded-sm p-3 space-y-3" data-testid="bank-sources">
      <div className="flex flex-wrap items-end gap-2">
        {field("masked_name", "Nama tampil rekening *", "w-48")}
        {field("bank", "Bank")}
        {field("account_number", "Nomor rekening")}
        {field("currency", "Mata uang", "w-20")}
        {field("gl_account_code", "Kode akun GL")}
        <Button size="sm" variant="outline" onClick={createAccount} data-testid="bank-new-create">Tambah rekening</Button>
      </div>
      <div className="flex flex-wrap items-end gap-2 border-t pt-3">
        <label className="text-xs"><span className="block text-muted-foreground mb-1">Rekening koran (PDF dari bank) untuk rekening terpilih</span>
          <Input type="file" accept=".pdf,application/pdf" className="h-9 w-72 max-w-full" onChange={(e) => setFile(e.target.files[0] || null)}
                 data-testid="bank-statement-file" disabled={!selected} />
        </label>
        <Button size="sm" disabled={!selected || !file || busy} onClick={upload} data-testid="bank-statement-upload">
          {busy ? "Mengunggah…" : "Unggah rekening koran"}
        </Button>
        {!selected && <span className="text-xs text-muted-foreground">Pilih atau tambah rekening terlebih dahulu.</span>}
      </div>
      {refusal && <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="bank-statement-refusal">{refusal}</p>}
      {result && (
        <div className="text-xs border rounded-sm p-2" data-testid="bank-statement-result">
          {result.bank} · {result.account_number} · periode {result.period} · {result.transaction_count} transaksi ·
          aritmetika saldo: <span className="font-semibold">{result.arithmetic_status}</span>
          {result.arithmetic_status !== "RECONCILED" && " — saldo awal + mutasi tidak sama dengan saldo akhir; periksa rekening koran."}
        </div>
      )}
    </div>
  );
}
